import { FC, useCallback, useMemo, useState } from 'react';

import { MagritteNotificationName } from 'utils/constants/notification';

import { Alert, Button, ButtonStack, TableBody, useBreakpoint } from '@hh.ru/magritte-ui';
import { TagStyle } from '@hh.ru/magritte-ui-tag/types';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { stopPromoCode } from 'components/Admin/PromoCodes/api/stopPromoCode';
import { PromoCodeModal } from 'components/Admin/PromoCodes/PromoCodeModal/PromoCodeModal';
import { EnrichedPromoCode, PromoCodeTableRow } from 'components/Admin/PromoCodes/PromoCodesTable/PromoCodeTableRow';
import {
    formatBound,
    formatPeriod,
    formatRemainingActivations,
} from 'components/Admin/PromoCodes/utils/formatTableContent';

import { addMagritteNotification } from 'store/models/notification';
import { PromoCode, PromoCodeStatus, updatePromoCode } from 'store/models/promoCodes';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    status: {
        [PromoCodeStatus.Active]: 'admin.promoCodes.status.active',
        [PromoCodeStatus.Pending]: 'admin.promoCodes.status.pending',
        [PromoCodeStatus.Expired]: 'admin.promoCodes.status.expired',
        [PromoCodeStatus.Exhausted]: 'admin.promoCodes.status.exhausted',
    },
};

const getPromoCodeStatusStyle = (status: PromoCodeStatus): TagStyle => {
    switch (status) {
        case PromoCodeStatus.Active:
            return 'positive';
        case PromoCodeStatus.Pending:
            return 'warning';
        case PromoCodeStatus.Expired:
        case PromoCodeStatus.Exhausted:
        default:
            return 'neutral';
    }
};

export const PromoCodeTableContent: FC = () => {
    const promoCodes = useSelector(({ promoCodes }) => promoCodes.promoCodes);

    const [targetPromoCode, setTargetPromoCode] = useState<string | null>(null);
    const [isStopAlertVisible, setIsStopAlertVisibleOn, setIsStopAlertVisibleOff] = useOnOffState(false);
    const [promoCodeToStop, setPromoCodeToStop] = useState<string | null>(null);
    const [loadingPromoCode, setLoadingPromoCode] = useState<string | null>(null);
    const [isEditModalVisible, setIsEditModalVisibleOn, setIsEditModalVisibleOff] = useOnOffState(false);
    const [promoCodeToEdit, setPromoCodeToEdit] = useState<PromoCode | null>(null);

    const trl = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();

    const enrichedPromoCodes = useMemo((): EnrichedPromoCode[] | undefined => {
        return promoCodes?.map((item) => ({
            ...item,
            style: getPromoCodeStatusStyle(item.status),
            statusText: trl(TrlKeys.status[item.status]),
            boundText: formatBound(item.bound),
            periodText: formatPeriod(item.startDate, item.endDate),
            remainingActivations: formatRemainingActivations(item.activationsLimit, item.activationsCount),
            canBeStopped: item.status === PromoCodeStatus.Active,
        }));
    }, [promoCodes, trl]);

    const handleOpenStopAlert = useCallback(
        (promoCode: string) => {
            setPromoCodeToStop(promoCode);
            setIsStopAlertVisibleOn();
        },
        [setIsStopAlertVisibleOn]
    );

    const handleCloseStopAlert = useCallback(() => {
        setIsStopAlertVisibleOff();
        setPromoCodeToStop(null);
    }, [setIsStopAlertVisibleOff]);

    const handleStopPromoCode = useCallback(async () => {
        if (!promoCodeToStop) {
            return;
        }

        setIsStopAlertVisibleOff();
        setPromoCodeToStop(null);
        setLoadingPromoCode(promoCodeToStop);

        try {
            await stopPromoCode(promoCodeToStop);
            dispatch(
                updatePromoCode({
                    promoCode: promoCodeToStop,
                    activationsLimit: 0,
                    status: PromoCodeStatus.Exhausted,
                })
            );
        } catch (error) {
            dispatch(addMagritteNotification(MagritteNotificationName.AdminStopPromoCodeError));
        } finally {
            setLoadingPromoCode(null);
        }
    }, [promoCodeToStop, setIsStopAlertVisibleOff, dispatch]);

    const handleMouseEnter = useCallback((promoCode: string) => {
        setTargetPromoCode(promoCode);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setTargetPromoCode(null);
    }, []);

    const handleEditClick = useCallback(
        (promoCode: PromoCode) => {
            setPromoCodeToEdit(promoCode);
            setIsEditModalVisibleOn();
        },
        [setIsEditModalVisibleOn]
    );

    const handleEditModalClose = useCallback(() => {
        setIsEditModalVisibleOff();
        setPromoCodeToEdit(null);
    }, [setIsEditModalVisibleOff]);

    const rows = (
        <>
            {enrichedPromoCodes?.map((item) => (
                <PromoCodeTableRow
                    key={item.promoCode}
                    item={item}
                    isTargeted={targetPromoCode === item.promoCode}
                    isLoading={loadingPromoCode === item.promoCode}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onStopClick={handleOpenStopAlert}
                    onEditClick={handleEditClick}
                />
            ))}
        </>
    );

    return (
        <>
            {isMobile ? rows : <TableBody>{rows}</TableBody>}
            <Alert
                title="Остановить действие промокода?"
                description="Позже вы сможете запустить его заново"
                buttons={
                    <ButtonStack stretched layout="vertical">
                        <Button mode="primary" size="medium" style="accent" stretched onClick={handleStopPromoCode}>
                            Да, остановить
                        </Button>
                        <Button mode="secondary" size="medium" style="accent" stretched onClick={handleCloseStopAlert}>
                            Отмена
                        </Button>
                    </ButtonStack>
                }
                visible={isStopAlertVisible}
                onClose={handleCloseStopAlert}
            />
            <PromoCodeModal
                visible={isEditModalVisible}
                onClose={handleEditModalClose}
                editPromoCode={promoCodeToEdit}
            />
        </>
    );
};
